import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Search,
  MoreVert,
  InfoOutlined,
  AdminPanelSettingsOutlined,
  RemoveModeratorOutlined,
  BlockOutlined,
  CheckCircleOutline,
  LockResetOutlined,
} from '@mui/icons-material';
import { useSupabase } from '../../contexts/SupabaseContext';
import { MODULES } from '../../config/modules';
import { adminErrorMessage, formatDay, formatDateTime, timeAgo, daysSince, moduleOfTable } from './adminShared';

const FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'admins', label: 'Admins' },
  { value: 'disabled', label: 'Disabled' },
  { value: 'new', label: 'New (30 days)' },
  { value: 'inactive', label: 'Inactive (90+ days)' },
];
const number = (n) => new Intl.NumberFormat('en-IN').format(n || 0);

// What each action does, shown in the confirmation dialog
const CONFIRM = {
  make_admin: {
    title: 'Make admin?',
    body: (u) => `${u.email} will be able to see all users, disable accounts, make other admins and switch modules on or off.`,
    button: 'Make admin', color: 'primary',
  },
  remove_admin: {
    title: 'Remove admin?',
    body: (u) => `${u.email} will lose access to the Admin pages. Their own data is not affected.`,
    button: 'Remove admin', color: 'error',
  },
  disable: {
    title: 'Disable this account?',
    body: (u) => `${u.email} won't be able to sign in, and will be signed out of every device within the hour. Their data is kept, and you can enable the account again at any time.`,
    button: 'Disable account', color: 'error', reason: true,
  },
  enable: {
    title: 'Enable this account?',
    body: (u) => `${u.email} will be able to sign in again.`,
    button: 'Enable account', color: 'primary',
  },
  reset: {
    title: 'Send a password reset email?',
    body: (u) => `A link to choose a new password will be emailed to ${u.email}. Their current password keeps working until they change it.`,
    button: 'Send email', color: 'primary',
  },
};

const AdminUsers = () => {
  const { supabase, user: me } = useSupabase();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  const [menu, setMenu] = useState({ anchor: null, user: null });
  const [confirm, setConfirm] = useState(null); // { action, user }
  const [reason, setReason] = useState('');
  const [working, setWorking] = useState(false);
  const [confirmError, setConfirmError] = useState('');
  const [details, setDetails] = useState(null); // { user, counts | null }
  const [snackbar, setSnackbar] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error: rpcError } = await supabase.rpc('admin_list_users');
    if (rpcError) setError(adminErrorMessage(rpcError));
    else { setUsers(data || []); setError(''); }
    setLoading(false);
  }, [supabase]);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => ({
    all: users.length,
    admins: users.filter(u => u.is_admin).length,
    disabled: users.filter(u => u.disabled).length,
    new: users.filter(u => daysSince(u.created_at) <= 30).length,
    inactive: users.filter(u => daysSince(u.last_sign_in_at) > 90).length,
  }), [users]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users
      .filter(u => filter === 'all'
        || (filter === 'admins' && u.is_admin)
        || (filter === 'disabled' && u.disabled)
        || (filter === 'new' && daysSince(u.created_at) <= 30)
        || (filter === 'inactive' && daysSince(u.last_sign_in_at) > 90))
      .filter(u => !q || (u.email || '').toLowerCase().includes(q) || (u.name || '').toLowerCase().includes(q));
  }, [users, search, filter]);

  const openConfirm = (action, user) => {
    setMenu({ anchor: null, user: null });
    setReason('');
    setConfirmError('');
    setConfirm({ action, user });
  };

  const runAction = async () => {
    const { action, user } = confirm;
    setWorking(true);
    setConfirmError('');
    let result;
    if (action === 'make_admin' || action === 'remove_admin') {
      result = await supabase.rpc('admin_set_admin', { p_user: user.id, p_make: action === 'make_admin' });
    } else if (action === 'disable' || action === 'enable') {
      result = await supabase.rpc('admin_set_disabled', { p_user: user.id, p_disabled: action === 'disable', p_reason: reason.trim() || null });
    } else if (action === 'reset') {
      result = await supabase.auth.resetPasswordForEmail(user.email);
      if (!result.error) await supabase.rpc('admin_record', { p_action: 'user.password_reset', p_target: user.email, p_details: null });
    }
    setWorking(false);
    if (result?.error) {
      setConfirmError(adminErrorMessage(result.error));
      return;
    }
    setConfirm(null);
    setSnackbar({
      make_admin: `${user.email} is now an admin`,
      remove_admin: `${user.email} is no longer an admin`,
      disable: `${user.email} has been disabled`,
      enable: `${user.email} has been enabled`,
      reset: `Password reset email sent to ${user.email}`,
    }[action]);
    if (action !== 'reset') load();
  };

  const openDetails = async (user) => {
    setMenu({ anchor: null, user: null });
    setDetails({ user, counts: null, error: '' });
    const { data, error: rpcError } = await supabase.rpc('admin_user_details', { p_user: user.id });
    setDetails(prev => (prev && prev.user.id === user.id ? { ...prev, counts: data || {}, error: rpcError ? adminErrorMessage(rpcError) : '' } : prev));
  };

  if (error) return <Alert severity="error">{error}</Alert>;

  const menuUser = menu.user;
  const isMe = (u) => u?.id === me?.id;
  const statusChips = (u) => (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
      {u.is_admin && <Chip size="small" color="primary" label="Admin" />}
      {u.disabled ? <Chip size="small" color="error" variant="outlined" label="Disabled" /> : <Chip size="small" color="success" variant="outlined" label="Active" />}
      {!u.email_confirmed && <Chip size="small" variant="outlined" label="Email not confirmed" />}
    </Box>
  );
  const actionsButton = (u) => (
    <IconButton size="small" aria-label={`Actions for ${u.email}`} onClick={(e) => setMenu({ anchor: e.currentTarget, user: u })}>
      <MoreVert fontSize="small" />
    </IconButton>
  );
  const nameCell = (u) => (
    <Box sx={{ minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{u.name || u.email.split('@')[0]}</Typography>
        {isMe(u) && <Chip size="small" label="You" sx={{ height: 18, fontSize: '0.625rem' }} />}
      </Box>
      <Typography variant="caption" color="text.secondary" component="div" noWrap>{u.email}</Typography>
    </Box>
  );

  // Details: record counts grouped by module
  const detailRows = details?.counts
    ? MODULES.map(m => ({
      ...m,
      total: Object.entries(details.counts).filter(([t]) => moduleOfTable(t) === m.key).reduce((s, [, c]) => s + Number(c || 0), 0),
    })).filter(m => m.key !== 'analysis' && m.key !== 'finance-calculator' && m.key !== 'credit-cards')
    : [];

  const confirmSpec = confirm && CONFIRM[confirm.action];

  return (
    <Box>
      <Paper data-tour="admin-user-filters" sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search name or email"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
          sx={{ minWidth: { xs: '100%', sm: 280 } }}
        />
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
          {FILTERS.map(f => (
            <Chip
              key={f.value}
              label={`${f.label} (${counts[f.value]})`}
              color={filter === f.value ? 'primary' : 'default'}
              variant={filter === f.value ? 'filled' : 'outlined'}
              onClick={() => setFilter(f.value)}
            />
          ))}
        </Box>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : shown.length === 0 ? (
        <Paper sx={{ p: 5, textAlign: 'center' }}><Typography color="text.secondary">No users match.</Typography></Paper>
      ) : isPhone ? (
        // Phones: one card per user
        <Box data-tour="admin-users" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {shown.map(u => (
            <Paper key={u.id} sx={{ p: 1.5, display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {nameCell(u)}
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                  Joined {formatDay(u.created_at)} · last sign-in {timeAgo(u.last_sign_in_at).toLowerCase()}
                </Typography>
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.75 }}>
                  {number(u.transactions)} transactions · {number(u.wallets)} wallets
                </Typography>
                {statusChips(u)}
              </Box>
              {actionsButton(u)}
            </Paper>
          ))}
        </Box>
      ) : (
        <Paper data-tour="admin-users" sx={{ overflow: 'hidden' }}>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" aria-label="Users">
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Joined</TableCell>
                  <TableCell>Last sign-in</TableCell>
                  <TableCell>Sign-in method</TableCell>
                  <TableCell align="right">Transactions</TableCell>
                  <TableCell align="right">Wallets</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {shown.map(u => (
                  <TableRow key={u.id} hover sx={{ opacity: u.disabled ? 0.7 : 1 }}>
                    <TableCell sx={{ maxWidth: 280 }}>{nameCell(u)}</TableCell>
                    <TableCell>{formatDay(u.created_at)}</TableCell>
                    <TableCell title={formatDateTime(u.last_sign_in_at)}>{timeAgo(u.last_sign_in_at)}</TableCell>
                    <TableCell sx={{ textTransform: 'capitalize' }}>{u.provider}</TableCell>
                    <TableCell align="right">{number(u.transactions)}</TableCell>
                    <TableCell align="right">{number(u.wallets)}</TableCell>
                    <TableCell>{statusChips(u)}</TableCell>
                    <TableCell align="right">{actionsButton(u)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Showing {shown.length} of {users.length} users. Admins can't see anyone's financial details here, only how many records they have.
      </Typography>

      {/* Actions */}
      <Menu anchorEl={menu.anchor} open={Boolean(menu.anchor)} onClose={() => setMenu({ anchor: null, user: null })}>
        {menuUser && [
          <MenuItem key="details" onClick={() => openDetails(menuUser)}>
            <ListItemIcon><InfoOutlined fontSize="small" /></ListItemIcon>View details
          </MenuItem>,
          <MenuItem key="reset" onClick={() => openConfirm('reset', menuUser)} disabled={menuUser.disabled}>
            <ListItemIcon><LockResetOutlined fontSize="small" /></ListItemIcon>Send password reset email
          </MenuItem>,
          menuUser.is_admin ? (
            <MenuItem key="admin" onClick={() => openConfirm('remove_admin', menuUser)} disabled={isMe(menuUser)}>
              <ListItemIcon><RemoveModeratorOutlined fontSize="small" /></ListItemIcon>
              {isMe(menuUser) ? "Remove admin (can't remove yourself)" : 'Remove admin'}
            </MenuItem>
          ) : (
            <MenuItem key="admin" onClick={() => openConfirm('make_admin', menuUser)} disabled={menuUser.disabled}>
              <ListItemIcon><AdminPanelSettingsOutlined fontSize="small" /></ListItemIcon>Make admin
            </MenuItem>
          ),
          menuUser.disabled ? (
            <MenuItem key="status" onClick={() => openConfirm('enable', menuUser)}>
              <ListItemIcon><CheckCircleOutline fontSize="small" color="success" /></ListItemIcon>Enable account
            </MenuItem>
          ) : (
            <MenuItem key="status" onClick={() => openConfirm('disable', menuUser)} disabled={isMe(menuUser) || menuUser.is_admin} sx={{ color: 'error.main' }}>
              <ListItemIcon><BlockOutlined fontSize="small" color="error" /></ListItemIcon>
              {isMe(menuUser) ? "Disable (can't disable yourself)" : menuUser.is_admin ? 'Disable (remove admin first)' : 'Disable account'}
            </MenuItem>
          ),
        ]}
      </Menu>

      {/* Confirm */}
      <Dialog open={Boolean(confirm)} onClose={() => !working && setConfirm(null)} maxWidth="xs" fullWidth>
        {confirmSpec && (
          <>
            <DialogTitle>{confirmSpec.title}</DialogTitle>
            <DialogContent>
              <DialogContentText>{confirmSpec.body(confirm.user)}</DialogContentText>
              {confirmSpec.reason && (
                <TextField
                  label="Reason (optional, kept in the activity log)"
                  fullWidth
                  size="small"
                  sx={{ mt: 2 }}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              )}
              {confirmError && <Alert severity="error" sx={{ mt: 2 }}>{confirmError}</Alert>}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirm(null)} disabled={working}>Cancel</Button>
              <Button variant="contained" color={confirmSpec.color} onClick={runAction} disabled={working}>
                {working ? 'Working…' : confirmSpec.button}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Details */}
      <Dialog open={Boolean(details)} onClose={() => setDetails(null)} maxWidth="xs" fullWidth>
        {details && (
          <>
            <DialogTitle>{details.user.name || details.user.email}</DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 2, rowGap: 0.5, mb: 2, '& dt': { color: 'text.secondary' }, '& dd': { m: 0, fontWeight: 600 } }} component="dl">
                <dt>Email</dt><dd>{details.user.email}</dd>
                <dt>Joined</dt><dd>{formatDateTime(details.user.created_at)}</dd>
                <dt>Last sign-in</dt><dd>{formatDateTime(details.user.last_sign_in_at)}</dd>
                <dt>Sign-in method</dt><dd style={{ textTransform: 'capitalize' }}>{details.user.provider}</dd>
                <dt>Email confirmed</dt><dd>{details.user.email_confirmed ? 'Yes' : 'No'}</dd>
              </Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Records by module</Typography>
              {details.error ? <Alert severity="error">{details.error}</Alert> : !details.counts ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} /></Box>
              ) : (
                <Table size="small">
                  <TableBody>
                    {detailRows.map(m => (
                      <TableRow key={m.key}>
                        <TableCell>{m.name}</TableCell>
                        <TableCell align="right">{number(m.total)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </DialogContent>
            <DialogActions><Button onClick={() => setDetails(null)}>Close</Button></DialogActions>
          </>
        )}
      </Dialog>

      <Snackbar open={Boolean(snackbar)} autoHideDuration={3500} onClose={() => setSnackbar('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" variant="filled" onClose={() => setSnackbar('')}>{snackbar}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminUsers;
